/**
 * whiteLabelBuilderLib.js
 * Pure constants and helpers for the Reseller Page Builder (WhiteLabel.js).
 * Extracted for maintainability — no React imports, no side effects.
 */
import {
  DEFAULT_SHARED_STOREFRONT_CONTENT,
  readSharedStorefrontContent,
} from '@/lib/whiteLabelSharedContent';

// ── Fonts ─────────────────────────────────────────────────────────────────────
export const BUILDER_FONTS = [
  { label: 'Inter', value: "'Inter', 'Segoe UI', Arial, sans-serif" },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
];

// ── Branding theme ────────────────────────────────────────────────────────────
export const BRANDING_THEME_STORAGE_KEY = 'cloudsetu_reseller_branding_theme';

export const readStoredBrandingTheme = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(BRANDING_THEME_STORAGE_KEY) || '{}');
    return {
      primary_color: saved.primary_color,
      accent_color: saved.accent_color,
      background_color: saved.background_color,
    };
  } catch (error) {
    console.error('Unable to read reseller branding colors.', error);
    return {};
  }
};

// ── Section library ───────────────────────────────────────────────────────────
export const BUILDER_LIBRARY = [
  {
    type: 'header',
    name: 'Header',
    variants: ['Logo Left / Navigation Right', 'Centered Navigation', 'Centered Brand', 'Announcement Bar', 'Minimal Header', 'Split Navigation', 'Mobile-first Header'],
    defaultVariant: 'Logo Left / Navigation Right',
  },
  {
    type: 'hero',
    name: 'Hero Section (Banner)',
    variants: ['Modern split', 'Centered Minimal', 'Dark Gradient Glow', 'Video / Media Frame', 'Stats Showcase'],
    defaultVariant: 'Modern split',
    heading: 'Build, scale, and grow your cloud business',
    subtext: 'Launch powerful cloud hosting services with a storefront designed for your enterprise customers.',
    primaryText: 'Get Started'
  },
  {
    type: 'features',
    name: 'Features',
    variants: ['3-Column Cards', '2-Column Split Image', 'Icon Row List', 'Bento Grid', 'Timeline Steps'],
    defaultVariant: '3-Column Cards',
    heading: 'Everything you need to succeed',
    subtext: 'Enterprise-grade infrastructure, automated orchestration, and 24/7 expert support.',
    primaryText: 'Explore Features'
  },
  {
    type: 'pricing',
    name: 'Pricing',
    variants: ['Three Tier Cards', 'Comparison Matrix Table', 'Compact Plan List', 'Monthly / Annual Switch', 'Featured Spotlight'],
    defaultVariant: 'Three Tier Cards',
    heading: 'Transparent plans for every scale',
    subtext: 'Flexible pricing models tailored for startups, growth agencies, and high-volume enterprises.',
    primaryText: 'Choose Plan'
  },
  {
    type: 'testimonials',
    name: 'Testimonials',
    variants: ['Three Column Cards', 'Spotlight Case Study', 'Quote Grid Wall', 'Trust Badges & Review Bar', 'Social Feed Cards'],
    defaultVariant: 'Three Column Cards',
    heading: 'Trusted by over 10,000 businesses',
    subtext: 'Discover how top companies accelerated their digital infrastructure with our platform.',
    primaryText: 'Read Stories'
  },
  {
    type: 'trust',
    name: 'Trust Logos',
    variants: ['Minimal Logo Strip', '2-Row Logo Grid', 'Metrics & Certifications', 'Badge Cards Row', 'Global Reach Counter'],
    defaultVariant: 'Minimal Logo Strip',
    heading: 'Powering innovative brands worldwide',
    subtext: 'Industry-leading security, 99.99% uptime guarantee, and SOC2 certified compliance.',
    primaryText: ''
  },
  {
    type: 'faq',
    name: 'FAQs',
    variants: ['Accordion List', 'Two Column Questions', 'Centered FAQ Cards', 'Category Tabs', 'Minimal Search & Answers'],
    defaultVariant: 'Accordion List',
    heading: 'Frequently asked questions',
    subtext: 'Everything you need to know before launching with CloudSetu.',
    primaryText: 'View All FAQs'
  },
  {
    type: 'blog',
    name: 'Blogs',
    variants: ['3-Column Article Cards', 'Featured Story + Grid', 'Magazine Grid', 'Horizontal Article List', 'Newsroom Timeline'],
    defaultVariant: '3-Column Article Cards',
    heading: 'Latest from our blog',
    subtext: 'Cloud insights, hosting guides, and product updates from our team.',
    primaryText: 'View All Articles'
  },
  {
    type: 'slider',
    name: 'Slider / Carousel',
    variants: ['Full Width Hero Slider', 'Content + Image Slider', 'Card Carousel', 'Thumbnail Slider', 'Testimonial Slider'],
    defaultVariant: 'Full Width Hero Slider',
    heading: 'Discover what is possible',
    subtext: 'Create a memorable experience with fully editable slides.',
    primaryText: 'Explore Now'
  },
  {
    type: 'cta',
    name: 'CTA Banner',
    variants: ['Centered High Impact', 'Split Dual Action', 'Floating Elevated Card', 'Gradient Glow', 'Lead Capture Form'],
    defaultVariant: 'Centered High Impact',
    heading: 'Ready to launch your platform today?',
    subtext: 'Join thousands of developers and businesses. Start your 14-day free trial with zero setup fees.',
    primaryText: 'Start Free Trial'
  },
  {
    type: 'footer',
    name: 'Footer',
    variants: ['Standard 4-Column', 'Minimal Clean Bar', 'Newsletter Subscription', 'App & Status Dashboard', 'Corporate Legal Bar'],
    defaultVariant: 'Standard 4-Column',
    heading: 'CloudSetu Technologies',
    subtext: 'Next-generation cloud infrastructure and reseller storefront platform.',
    primaryText: 'Contact Us'
  },
  {
    type: 'products',
    name: 'Our Products',
    variants: ['Product Grid', 'Featured Spotlight', 'Category Tabs', 'Pricing Cards', 'Compact List', 'Comparison Table', 'Full Catalog'],
    defaultVariant: 'Product Grid',
    heading: 'Our Products & Services',
    subtext: 'Choose from our range of cloud services and SaaS solutions.',
    primaryText: 'Get Started',
  },
];

// ── Template helpers ──────────────────────────────────────────────────────────
export const getDefaultBuilderCards = (type) => {
  return JSON.parse(JSON.stringify(DEFAULT_SHARED_STOREFRONT_CONTENT[type]?.cards || []));
};

export const getConfiguredDefaultSectionTypes = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('cloudsetu_admin_global_settings') || '{}');
    return Array.isArray(settings.starterSectionTypes) ? settings.starterSectionTypes : null;
  } catch {
    return null;
  }
};

export const getAdminTemplateSections = templateIndex => {
  try {
    const builder = JSON.parse(localStorage.getItem('cloudsetu_admin_page_builder') || 'null');
    const page = builder?.pages?.[templateIndex];
    return Array.isArray(page?.sections) ? page.sections : Array.isArray(page) ? page : null;
  } catch (error) {
    console.error('Unable to read Admin-approved page sections.', error);
    return [];
  }
};

export const getAdminTemplateCatalog = () => {
  try {
    const builder = JSON.parse(localStorage.getItem('cloudsetu_admin_page_builder') || 'null');
    const pages = builder?.pages && typeof builder.pages === 'object' ? builder.pages : {};
    return Object.entries(pages)
      .filter(([index, page]) => Number.isInteger(Number(index)) && Number(index) >= 0
        && (Array.isArray(page?.sections) || Array.isArray(page)))
      .map(([index, page]) => ({
        index: Number(index),
        name: page?.name || `Site ${Number(index) + 1}`,
        sections: Array.isArray(page?.sections) ? page.sections : page,
      }))
      .sort((left, right) => left.index - right.index);
  } catch (error) {
    console.error('Unable to load Admin-approved designs.', error);
    return [];
  }
};

export const applyResellerContentOverrides = (sections, overrides) => (Array.isArray(sections) ? sections : []).map(section => {
  const sectionOverrides = overrides?.[section.type] || {};
  return {
    ...section,
    ...sectionOverrides,
    contentOverrides: { ...sectionOverrides },
  };
});

export const readResellerContentOverrides = saved => {
  const stored = saved?.contentOverridesBySectionType;
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) return stored;

  const activeIndex = Number(saved?.activeTemplatePage) || 0;
  const activePage = saved?.pages?.[activeIndex];
  const resellerSections = Array.isArray(activePage?.sections) ? activePage.sections
    : Array.isArray(activePage) ? activePage
      : Array.isArray(saved?.sections) ? saved.sections : [];
  const adminSections = getAdminTemplateSections(activeIndex) || [];
  const resellerDefaults = saved?.sharedContent || {};
  const migrated = {};
  const contentFields = ['kicker', 'heading', 'subtext', 'primaryText', 'secondaryText', 'cards', 'inlineTextEdits'];

  resellerSections.forEach(section => {
    const candidates = section.contentOverrides;
    if (!candidates || typeof candidates !== 'object') return;
    const adminSection = adminSections.find(item => item.type === section.type);
    const differences = {};
    contentFields.forEach(field => {
      if (!Object.prototype.hasOwnProperty.call(candidates, field)) return;
      const baseline = adminSection?.contentOverrides?.[field]
        ?? adminSection?.[field]
        ?? resellerDefaults[section.type]?.[field];
      if (JSON.stringify(candidates[field]) !== JSON.stringify(baseline)) differences[field] = candidates[field];
    });
    if (Object.keys(differences).length) {
      migrated[section.type] = { ...migrated[section.type], ...differences };
    }
  });
  return migrated;
};

export const createCompleteTemplatePage = (templateIndex) => {
  const defaultTypes = getConfiguredDefaultSectionTypes();
  let adminLibrary = null;
  try {
    adminLibrary = JSON.parse(localStorage.getItem('cloudsetu_admin_section_library') || 'null');
  } catch (error) {
    console.error('Unable to load Admin section availability.', error);
  }
  if (!Array.isArray(adminLibrary)) return [];
  const adminSections = getAdminTemplateSections(templateIndex);
  if (!adminSections) return [];
  if (!adminSections.some(section => section.type === 'header')) {
    const headerLibraryEntry = adminLibrary.find(item => item.type === 'header');
    if (headerLibraryEntry?.enabled !== false && !headerLibraryEntry?.deleted) {
      adminSections.unshift({
        id: `admin-sec-header-page-${templateIndex + 1}`,
        type: 'header',
        name: headerLibraryEntry?.name || 'Header',
        variant: headerLibraryEntry?.variants?.[0] || 'Logo Left / Navigation Right',
        visible: true,
        order: -1,
      });
    }
  }
  const sharedContent = readSharedStorefrontContent();
  return adminSections.flatMap(adminSection => {
    const definition = BUILDER_LIBRARY.find(item => item.type === adminSection.type);
    const configured = adminLibrary.find(item => item.type === adminSection.type);
    if (!definition || !configured || configured.enabled === false || configured.deleted
      || (adminSection.type !== 'header' && defaultTypes && !defaultTypes.includes(adminSection.type))) return [];
    const names = Array.isArray(configured.variants) && typeof configured.variants[0] === 'string'
      ? configured.variants
      : Array.isArray(configured.variantNames) ? configured.variantNames : [];
    const disabled = Array.isArray(configured.disabledVariants) ? configured.disabledVariants : [];
    const availableVariants = names.filter(name => !disabled.includes(name));
    if (!availableVariants.length) return [];
    const content = sharedContent[adminSection.type] || DEFAULT_SHARED_STOREFRONT_CONTENT[adminSection.type] || definition;
    const { contentOverrides, ...layout } = adminSection;
    return [{
      ...layout,
      id: `sec-${adminSection.type}-page-${templateIndex + 1}`,
      name: configured.name || definition.name,
      variant: availableVariants.includes(adminSection.variant) ? adminSection.variant : availableVariants[0],
      kicker: content.kicker || '',
      heading: content.heading || '',
      subtext: content.subtext || '',
      primaryText: content.primaryText || '',
      secondaryText: content.secondaryText || '',
      cards: content.cards || getDefaultBuilderCards(adminSection.type),
      navigation: content.navigation || [],
      actions: content.actions || [],
      visible: adminSection.visible !== false,
    }];
  });
};

export const getConfiguredSectionLibrary = (adminLibrary, partnerTier) => BUILDER_LIBRARY.map(section => {
  const adminSection = Array.isArray(adminLibrary)
    ? adminLibrary.find(item => item.type === section.type)
    : null;
  if (!adminSection) return { ...section, variants: [], enabled: false, tierAllowed: false };
  const configuredVariants = Array.isArray(adminSection.variantNames)
    ? adminSection.variantNames
    : Array.isArray(adminSection.variants) && typeof adminSection.variants[0] === 'string'
      ? adminSection.variants
      : [];
  const disabledVariants = Array.isArray(adminSection.disabledVariants) ? adminSection.disabledVariants : [];
  return {
    ...section,
    name: adminSection.name || section.name,
    variants: configuredVariants.filter(name => !disabledVariants.includes(name)),
    enabled: adminSection.enabled !== false && !adminSection.deleted,
    tierAllowed: (adminSection.tiers || section.tiers) === 'All tiers'
      || (adminSection.tiers || section.tiers) === partnerTier
      || partnerTier === 'Premium',
  };
});

export const applyAdminSectionAvailability = (sections, adminLibrary, partnerTier) => {
  const accessible = getConfiguredSectionLibrary(adminLibrary, partnerTier)
    .filter(section => section.enabled && section.tierAllowed && section.variants.length);
  return (Array.isArray(sections) ? sections : []).flatMap(section => {
    const librarySection = accessible.find(item => item.type === section.type);
    if (!librarySection) return [];
    return [{
      ...section,
      name: librarySection.name,
      variant: librarySection.variants.includes(section.variant) ? section.variant : librarySection.variants[0],
    }];
  });
};

export const readAdminBuilderSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('cloudsetu_admin_settings') || '{}');
    return { showRightEditorInBuilder: saved.showRightEditorInBuilder !== false };
  } catch {
    return { showRightEditorInBuilder: true };
  }
};

/**
 * Check which section types from the admin template are missing in the
 * reseller's current published sections. Returns an array of section names.
 */
export const getMissingSectionsForAudit = (currentSections, templateIndex) => {
  const adminTemplateSections = getAdminTemplateSections(templateIndex) || [];
  const currentTypes = new Set((Array.isArray(currentSections) ? currentSections : []).map(s => s.type));
  return adminTemplateSections
    .filter(adminSection => !currentTypes.has(adminSection.type))
    .map(adminSection => {
      const lib = BUILDER_LIBRARY.find(item => item.type === adminSection.type);
      return lib?.name || adminSection.name || adminSection.type;
    });
};
